/**
 * Admin CRUD page for SS indicators/questions and AKHLAK values/behaviors.
 * Mirrors the Excel form layout with inline add/edit/delete.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { penilaianApi } from '../api/penilaianApi'
import type { SSIndicator, AkhlakValue } from '../types/penilaian.types'

type Tab = 'ss' | 'akhlak'

// ─── tiny shared components ───────────────────────────────────────────────────

function IconBtn({
  onClick,
  title,
  children,
  danger,
}: {
  onClick: () => void
  title: string
  children: React.ReactNode
  danger?: boolean
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`p-1 rounded transition-colors ${
        danger
          ? 'text-gray-300 hover:text-red-500 hover:bg-red-50'
          : 'text-gray-300 hover:text-blue-600 hover:bg-blue-50'
      }`}
    >
      {children}
    </button>
  )
}

function PencilIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15.232 5.232l3.536 3.536M9 11l6.071-6.071a2 2 0 112.829 2.829L11.828 13.83a4 4 0 01-1.932 1.05l-2.679.536.536-2.679A4 4 0 019 11z" />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  )
}

// ─── AKHLAK tab ───────────────────────────────────────────────────────────────

function AkhlakTab() {
  const qc = useQueryClient()

  const { data: values = [], isLoading } = useQuery({
    queryKey: ['akhlak-values'],
    queryFn: () => penilaianApi.getAkhlakValues(),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['akhlak-values'] })

  const createValue = useMutation({
    mutationFn: penilaianApi.createAkhlakValue,
    onSuccess: () => { invalidate(); toast.success('Core value ditambahkan') },
    onError: () => toast.error('Gagal menambah core value'),
  })
  const updateValue = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => penilaianApi.updateAkhlakValue(id, data),
    onSuccess: () => { invalidate(); toast.success('Disimpan') },
    onError: () => toast.error('Gagal menyimpan'),
  })
  const deleteValue = useMutation({
    mutationFn: penilaianApi.deleteAkhlakValue,
    onSuccess: () => { invalidate(); toast.success('Core value dihapus') },
    onError: () => toast.error('Gagal menghapus'),
  })
  const createBehavior = useMutation({
    mutationFn: ({ valueId, data }: { valueId: string; data: any }) =>
      penilaianApi.createAkhlakBehavior(valueId, data),
    onSuccess: () => { invalidate(); toast.success('Perilaku ditambahkan') },
    onError: () => toast.error('Gagal menambah perilaku'),
  })
  const updateBehavior = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => penilaianApi.updateAkhlakBehavior(id, data),
    onSuccess: () => { invalidate(); toast.success('Disimpan') },
    onError: () => toast.error('Gagal menyimpan'),
  })
  const deleteBehavior = useMutation({
    mutationFn: penilaianApi.deleteAkhlakBehavior,
    onSuccess: () => { invalidate(); toast.success('Perilaku dihapus') },
    onError: () => toast.error('Gagal menghapus'),
  })

  // local edit state
  const [editValueId, setEditValueId] = useState<string | null>(null)
  const [editValueDraft, setEditValueDraft] = useState<Partial<AkhlakValue>>({})
  const [editBehaviorId, setEditBehaviorId] = useState<string | null>(null)
  const [editBehaviorText, setEditBehaviorText] = useState('')
  const [addingBehaviorValueId, setAddingBehaviorValueId] = useState<string | null>(null)
  const [newBehaviorText, setNewBehaviorText] = useState('')
  const [addingValue, setAddingValue] = useState(false)
  const [newValue, setNewValue] = useState({ code: '', name: '', description: '' })

  if (isLoading) return <div className="py-12 text-center text-gray-500">Memuat...</div>

  const confirmDelete = (label: string, fn: () => void) => {
    if (window.confirm(`Hapus "${label}"?`)) fn()
  }

  const nextValueOrder = Math.max(0, ...values.map((v) => v.order_no)) + 1

  return (
    <div className="space-y-4">
      {/* Add new value button */}
      <div className="flex justify-end">
        {!addingValue && (
          <button
            onClick={() => setAddingValue(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >
            <PlusIcon /> Tambah Core Value
          </button>
        )}
      </div>

      {/* Add value form */}
      {addingValue && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-blue-800">Core Value Baru</p>
          <div className="flex gap-3">
            <input
              autoFocus
              placeholder="Kode (mis. AMANAH)"
              value={newValue.code}
              onChange={(e) => setNewValue({ ...newValue, code: e.target.value.toUpperCase() })}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-40"
            />
            <input
              placeholder="Nama"
              value={newValue.name}
              onChange={(e) => setNewValue({ ...newValue, name: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1"
            />
            <input
              placeholder="Deskripsi (opsional)"
              value={newValue.description}
              onChange={(e) => setNewValue({ ...newValue, description: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { setAddingValue(false); setNewValue({ code: '', name: '', description: '' }) }}
              className="px-3 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50"
            >
              Batal
            </button>
            <button
              disabled={!newValue.code || !newValue.name || createValue.isPending}
              onClick={() => {
                createValue.mutate(
                  { code: newValue.code, name: newValue.name, description: newValue.description || null, order_no: nextValueOrder },
                  { onSuccess: () => { setAddingValue(false); setNewValue({ code: '', name: '', description: '' }) } }
                )
              }}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Simpan
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#BDD7EE] text-gray-900">
              <th className="border border-gray-300 px-3 py-2 text-center font-semibold w-36">Core Value</th>
              <th className="border border-gray-300 px-3 py-2 text-center font-semibold">Pedoman Perilaku</th>
              <th className="border border-gray-300 px-3 py-2 text-center font-semibold w-20">
                Selalu<div className="text-xs font-normal text-gray-600">4</div>
              </th>
              <th className="border border-gray-300 px-3 py-2 text-center font-semibold w-20">
                Sering<div className="text-xs font-normal text-gray-600">3</div>
              </th>
              <th className="border border-gray-300 px-3 py-2 text-center font-semibold w-20">
                Jarang<div className="text-xs font-normal text-gray-600">2</div>
              </th>
              <th className="border border-gray-300 px-3 py-2 text-center font-semibold w-24">
                Tidak Pernah<div className="text-xs font-normal text-gray-600">1</div>
              </th>
              <th className="border border-gray-300 px-3 py-2 text-center font-semibold w-36">Tindakan Aktual</th>
              <th className="border border-gray-300 px-2 py-2 w-16" />
            </tr>
          </thead>
          <tbody>
            {values.map((value) => {
              const isEditingValue = editValueId === value.id
              const behaviors = value.behaviors ?? []
              // rows: behaviors + (adding row?) + Subtotal + Skor + Total Skor Awal + Catatan + SKOR AKHIR
              const extraRows = behaviors.length + (addingBehaviorValueId === value.id ? 1 : 0) + 5

              return (
                <>
                  {behaviors.map((b, i) => {
                    const isEditingB = editBehaviorId === b.id
                    return (
                      <tr key={b.id} className="bg-white hover:bg-blue-50/20">
                        {i === 0 && (
                          <td
                            rowSpan={extraRows}
                            className="border border-gray-300 px-2 py-2 text-center bg-[#BDD7EE]/30 align-top"
                          >
                            {isEditingValue ? (
                              <div className="space-y-1">
                                <input
                                  value={editValueDraft.code ?? ''}
                                  onChange={(e) => setEditValueDraft({ ...editValueDraft, code: e.target.value.toUpperCase() })}
                                  className="border border-gray-300 rounded px-1.5 py-1 text-xs w-full text-center font-bold"
                                  placeholder="Kode"
                                />
                                <input
                                  value={editValueDraft.name ?? ''}
                                  onChange={(e) => setEditValueDraft({ ...editValueDraft, name: e.target.value })}
                                  className="border border-gray-300 rounded px-1.5 py-1 text-xs w-full"
                                  placeholder="Nama"
                                />
                                <textarea
                                  value={editValueDraft.description ?? ''}
                                  onChange={(e) => setEditValueDraft({ ...editValueDraft, description: e.target.value })}
                                  className="border border-gray-300 rounded px-1.5 py-1 text-xs w-full resize-none"
                                  rows={2}
                                  placeholder="Deskripsi"
                                />
                                <div className="flex gap-1 justify-center">
                                  <button
                                    onClick={() => setEditValueId(null)}
                                    className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
                                  >
                                    Batal
                                  </button>
                                  <button
                                    disabled={updateValue.isPending}
                                    onClick={() => {
                                      updateValue.mutate(
                                        { id: value.id, data: { code: editValueDraft.code, name: editValueDraft.name, description: editValueDraft.description } },
                                        { onSuccess: () => setEditValueId(null) }
                                      )
                                    }}
                                    className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                  >
                                    Simpan
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="font-bold text-gray-800 text-xs">
                                  {value.name.toUpperCase()}
                                </div>
                                {value.description && (
                                  <div className="text-xs text-gray-400 mt-0.5">{value.description}</div>
                                )}
                                <div className="flex justify-center gap-0.5 mt-1">
                                  <IconBtn
                                    title="Edit core value"
                                    onClick={() => {
                                      setEditValueId(value.id)
                                      setEditValueDraft({ code: value.code, name: value.name, description: value.description ?? '' })
                                    }}
                                  >
                                    <PencilIcon />
                                  </IconBtn>
                                  <IconBtn
                                    danger
                                    title="Hapus core value"
                                    onClick={() => confirmDelete(value.name, () => deleteValue.mutate(value.id))}
                                  >
                                    <TrashIcon />
                                  </IconBtn>
                                </div>
                              </div>
                            )}
                          </td>
                        )}

                        {/* Behavior text cell */}
                        <td className="border border-gray-300 px-3 py-1.5 text-gray-700">
                          {isEditingB ? (
                            <div className="flex gap-2 items-center">
                              <input
                                autoFocus
                                value={editBehaviorText}
                                onChange={(e) => setEditBehaviorText(e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
                              />
                              <button
                                onClick={() => setEditBehaviorId(null)}
                                className="text-xs text-gray-500 hover:text-gray-700 px-1"
                              >
                                Batal
                              </button>
                              <button
                                disabled={updateBehavior.isPending}
                                onClick={() =>
                                  updateBehavior.mutate(
                                    { id: b.id, data: { behavior_text: editBehaviorText } },
                                    { onSuccess: () => setEditBehaviorId(null) }
                                  )
                                }
                                className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                Simpan
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between gap-1 group">
                              <span>{i + 1}. {b.behavior_text}</span>
                              <div className="flex gap-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                <IconBtn
                                  title="Edit perilaku"
                                  onClick={() => { setEditBehaviorId(b.id); setEditBehaviorText(b.behavior_text) }}
                                >
                                  <PencilIcon />
                                </IconBtn>
                                <IconBtn
                                  danger
                                  title="Hapus perilaku"
                                  onClick={() => confirmDelete(b.behavior_text, () => deleteBehavior.mutate(b.id))}
                                >
                                  <TrashIcon />
                                </IconBtn>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="border border-gray-300 bg-blue-50/40" />
                        <td className="border border-gray-300 bg-blue-50/40" />
                        <td className="border border-gray-300 bg-blue-50/40" />
                        <td className="border border-gray-300 bg-blue-50/40" />
                        <td className="border border-gray-300 bg-blue-50/40" />
                        <td className="border border-gray-300 bg-blue-50/40" />
                      </tr>
                    )
                  })}

                  {/* Add behavior inline row */}
                  {addingBehaviorValueId === value.id && (
                    <tr key={`add-b-${value.id}`} className="bg-yellow-50">
                      <td className="border border-gray-300 px-3 py-1.5" colSpan={6}>
                        <div className="flex gap-2 items-center">
                          <input
                            autoFocus
                            value={newBehaviorText}
                            onChange={(e) => setNewBehaviorText(e.target.value)}
                            placeholder="Teks perilaku baru..."
                            className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && newBehaviorText.trim()) {
                                createBehavior.mutate(
                                  { valueId: value.id, data: { behavior_text: newBehaviorText, order_no: behaviors.length + 1 } },
                                  { onSuccess: () => { setAddingBehaviorValueId(null); setNewBehaviorText('') } }
                                )
                              }
                              if (e.key === 'Escape') { setAddingBehaviorValueId(null); setNewBehaviorText('') }
                            }}
                          />
                          <button
                            onClick={() => { setAddingBehaviorValueId(null); setNewBehaviorText('') }}
                            className="text-xs text-gray-500 px-1 hover:text-gray-700"
                          >
                            Batal
                          </button>
                          <button
                            disabled={!newBehaviorText.trim() || createBehavior.isPending}
                            onClick={() =>
                              createBehavior.mutate(
                                { valueId: value.id, data: { behavior_text: newBehaviorText, order_no: behaviors.length + 1 } },
                                { onSuccess: () => { setAddingBehaviorValueId(null); setNewBehaviorText('') } }
                              )
                            }
                            className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            Tambah
                          </button>
                        </div>
                      </td>
                      <td className="border border-gray-300" />
                      <td className="border border-gray-300" />
                    </tr>
                  )}

                  {/* Summary rows */}
                  <tr key={`sub-${value.id}`} className="bg-gray-50 text-center text-xs text-gray-500">
                    <td className="border border-gray-300 px-3 py-1 text-right font-medium">Subtotal</td>
                    <td className="border border-gray-300" />
                    <td className="border border-gray-300" />
                    <td className="border border-gray-300" />
                    <td className="border border-gray-300" />
                    <td className="border border-gray-300" />
                    <td className="border border-gray-300 text-left">
                      <button
                        onClick={() => { setAddingBehaviorValueId(value.id); setNewBehaviorText('') }}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 px-1"
                      >
                        <PlusIcon /> Tambah perilaku
                      </button>
                    </td>
                  </tr>
                  <tr key={`skor-${value.id}`} className="bg-gray-50 text-center text-xs text-gray-500">
                    <td className="border border-gray-300 px-3 py-1 text-right font-medium">Skor</td>
                    <td className="border border-gray-300" /><td className="border border-gray-300" />
                    <td className="border border-gray-300" /><td className="border border-gray-300" />
                    <td className="border border-gray-300" /><td className="border border-gray-300" />
                  </tr>
                  <tr key={`tsa-${value.id}`} className="bg-gray-50 text-xs text-gray-500">
                    <td className="border border-gray-300 px-3 py-1 text-right font-medium" colSpan={2}>Total Skor Awal</td>
                    <td className="border border-gray-300" colSpan={5} />
                  </tr>
                  <tr key={`cat-${value.id}`} className="bg-gray-50 text-xs text-gray-500">
                    <td className="border border-gray-300 px-3 py-1 text-right" colSpan={2}>Catatan</td>
                    <td className="border border-gray-300" colSpan={5} />
                  </tr>
                  <tr key={`akhir-${value.id}`} className="bg-[#BDD7EE]/40 font-semibold text-xs text-gray-800">
                    <td className="border border-gray-300 px-3 py-1.5 text-right" colSpan={2}>SKOR AKHIR</td>
                    <td className="border border-gray-300 text-center text-blue-700" colSpan={5}>—</td>
                  </tr>
                </>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Soft Skill tab ───────────────────────────────────────────────────────────

const SS_SCORE_COLS = [
  { label: 'Excellent', value: 4 },
  { label: 'Meet Expectation', value: 3 },
  { label: 'Below Expectation', value: 2 },
  { label: 'Unsatisfactory', value: 1 },
]

function SSTab() {
  const qc = useQueryClient()

  const { data: indicators = [], isLoading } = useQuery({
    queryKey: ['ss-indicators'],
    queryFn: () => penilaianApi.getSSIndicators(),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['ss-indicators'] })

  const createIndicator = useMutation({
    mutationFn: penilaianApi.createSSIndicator,
    onSuccess: () => { invalidate(); toast.success('Indikator ditambahkan') },
    onError: () => toast.error('Gagal menambah indikator'),
  })
  const updateIndicator = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => penilaianApi.updateSSIndicator(id, data),
    onSuccess: () => { invalidate(); toast.success('Disimpan') },
    onError: () => toast.error('Gagal menyimpan'),
  })
  const deleteIndicator = useMutation({
    mutationFn: penilaianApi.deleteSSIndicator,
    onSuccess: () => { invalidate(); toast.success('Indikator dihapus') },
    onError: () => toast.error('Gagal menghapus'),
  })
  const createQuestion = useMutation({
    mutationFn: ({ indicatorId, data }: { indicatorId: string; data: any }) =>
      penilaianApi.createSSQuestion(indicatorId, data),
    onSuccess: () => { invalidate(); toast.success('Pertanyaan ditambahkan') },
    onError: () => toast.error('Gagal menambah pertanyaan'),
  })
  const updateQuestion = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => penilaianApi.updateSSQuestion(id, data),
    onSuccess: () => { invalidate(); toast.success('Disimpan') },
    onError: () => toast.error('Gagal menyimpan'),
  })
  const deleteQuestion = useMutation({
    mutationFn: penilaianApi.deleteSSQuestion,
    onSuccess: () => { invalidate(); toast.success('Pertanyaan dihapus') },
    onError: () => toast.error('Gagal menghapus'),
  })

  const [editIndId, setEditIndId] = useState<string | null>(null)
  const [editIndDraft, setEditIndDraft] = useState<Partial<SSIndicator>>({})
  const [editQId, setEditQId] = useState<string | null>(null)
  const [editQText, setEditQText] = useState('')
  const [addingQIndId, setAddingQIndId] = useState<string | null>(null)
  const [newQText, setNewQText] = useState('')
  const [addingInd, setAddingInd] = useState(false)
  const [newInd, setNewInd] = useState({ name: '', description: '', bobot: '' })

  if (isLoading) return <div className="py-12 text-center text-gray-500">Memuat...</div>

  const confirmDelete = (label: string, fn: () => void) => {
    if (window.confirm(`Hapus "${label}"?`)) fn()
  }

  const nextIndOrder = Math.max(0, ...indicators.map((i) => i.order_no)) + 1

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {!addingInd && (
          <button
            onClick={() => setAddingInd(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >
            <PlusIcon /> Tambah Indikator
          </button>
        )}
      </div>

      {addingInd && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-blue-800">Indikator Baru</p>
          <div className="flex gap-3">
            <input
              autoFocus
              placeholder="Nama indikator"
              value={newInd.name}
              onChange={(e) => setNewInd({ ...newInd, name: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1"
            />
            <input
              placeholder="Deskripsi (opsional)"
              value={newInd.description}
              onChange={(e) => setNewInd({ ...newInd, description: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1"
            />
            <div className="flex items-center gap-1">
              <input
                type="number"
                placeholder="Bobot %"
                value={newInd.bobot}
                onChange={(e) => setNewInd({ ...newInd, bobot: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-24"
                min={1} max={100}
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { setAddingInd(false); setNewInd({ name: '', description: '', bobot: '' }) }}
              className="px-3 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50"
            >
              Batal
            </button>
            <button
              disabled={!newInd.name || !newInd.bobot || createIndicator.isPending}
              onClick={() => {
                createIndicator.mutate(
                  { name: newInd.name, description: newInd.description || null, order_no: nextIndOrder, bobot: parseFloat(newInd.bobot) / 100 },
                  { onSuccess: () => { setAddingInd(false); setNewInd({ name: '', description: '', bobot: '' }) } }
                )
              }}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Simpan
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#BDD7EE] text-gray-900">
              <th className="border border-gray-300 px-3 py-2 text-center font-semibold w-36">Indikator</th>
              <th className="border border-gray-300 px-3 py-2 text-center font-semibold">Pertanyaan</th>
              {SS_SCORE_COLS.map((c) => (
                <th key={c.value} className="border border-gray-300 px-2 py-2 text-center font-semibold w-28">
                  {c.label}<div className="text-xs font-normal text-gray-600">{c.value}</div>
                </th>
              ))}
              <th className="border border-gray-300 px-3 py-2 text-center font-semibold w-32">Catatan</th>
              <th className="border border-gray-300 px-2 py-2 w-12" />
            </tr>
          </thead>
          <tbody>
            {indicators.map((ind) => {
              const isEditingInd = editIndId === ind.id
              const questions = ind.questions ?? []
              const extraRows = questions.length + (addingQIndId === ind.id ? 1 : 0) + 2

              return (
                <>
                  {questions.map((q, qi) => {
                    const isEditingQ = editQId === q.id
                    return (
                      <tr key={q.id} className="bg-white hover:bg-blue-50/20">
                        {qi === 0 && (
                          <td
                            rowSpan={extraRows}
                            className="border border-gray-300 px-2 py-2 text-center bg-[#BDD7EE]/30 align-top"
                          >
                            {isEditingInd ? (
                              <div className="space-y-1">
                                <input
                                  value={editIndDraft.name ?? ''}
                                  onChange={(e) => setEditIndDraft({ ...editIndDraft, name: e.target.value })}
                                  className="border border-gray-300 rounded px-1.5 py-1 text-xs w-full font-bold"
                                  placeholder="Nama"
                                />
                                <textarea
                                  value={editIndDraft.description ?? ''}
                                  onChange={(e) => setEditIndDraft({ ...editIndDraft, description: e.target.value })}
                                  className="border border-gray-300 rounded px-1.5 py-1 text-xs w-full resize-none"
                                  rows={2}
                                  placeholder="Deskripsi"
                                />
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    value={editIndDraft.bobot != null ? Math.round(editIndDraft.bobot * 100) : ''}
                                    onChange={(e) => setEditIndDraft({ ...editIndDraft, bobot: parseFloat(e.target.value) / 100 })}
                                    className="border border-gray-300 rounded px-1.5 py-1 text-xs w-full"
                                    placeholder="Bobot %"
                                    min={1} max={100}
                                  />
                                  <span className="text-xs text-gray-500 flex-shrink-0">%</span>
                                </div>
                                <div className="flex gap-1 justify-center">
                                  <button onClick={() => setEditIndId(null)} className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50">Batal</button>
                                  <button
                                    disabled={updateIndicator.isPending}
                                    onClick={() =>
                                      updateIndicator.mutate(
                                        { id: ind.id, data: { name: editIndDraft.name, description: editIndDraft.description, bobot: editIndDraft.bobot } },
                                        { onSuccess: () => setEditIndId(null) }
                                      )
                                    }
                                    className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                  >
                                    Simpan
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="font-bold text-gray-800 text-xs">{ind.name}</div>
                                {ind.bobot != null && (
                                  <div className="text-xs text-gray-500">Bobot {Math.round(ind.bobot * 100)}%</div>
                                )}
                                {ind.description && (
                                  <div className="text-xs text-gray-400 mt-0.5">{ind.description}</div>
                                )}
                                <div className="flex justify-center gap-0.5 mt-1">
                                  <IconBtn title="Edit indikator" onClick={() => { setEditIndId(ind.id); setEditIndDraft({ name: ind.name, description: ind.description ?? '', bobot: ind.bobot }) }}>
                                    <PencilIcon />
                                  </IconBtn>
                                  <IconBtn danger title="Hapus indikator" onClick={() => confirmDelete(ind.name, () => deleteIndicator.mutate(ind.id))}>
                                    <TrashIcon />
                                  </IconBtn>
                                </div>
                              </div>
                            )}
                          </td>
                        )}

                        <td className="border border-gray-300 px-3 py-1.5 text-gray-700">
                          {isEditingQ ? (
                            <div className="flex gap-2 items-center">
                              <textarea
                                autoFocus
                                value={editQText}
                                onChange={(e) => setEditQText(e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1 text-sm flex-1 resize-none"
                                rows={2}
                              />
                              <button onClick={() => setEditQId(null)} className="text-xs text-gray-500 hover:text-gray-700 px-1">Batal</button>
                              <button
                                disabled={updateQuestion.isPending}
                                onClick={() => updateQuestion.mutate({ id: q.id, data: { question_text: editQText } }, { onSuccess: () => setEditQId(null) })}
                                className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                Simpan
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between gap-1 group">
                              <span>{qi + 1}. {q.question_text}</span>
                              <div className="flex gap-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                <IconBtn title="Edit pertanyaan" onClick={() => { setEditQId(q.id); setEditQText(q.question_text) }}>
                                  <PencilIcon />
                                </IconBtn>
                                <IconBtn danger title="Hapus pertanyaan" onClick={() => confirmDelete(q.question_text.slice(0, 40), () => deleteQuestion.mutate(q.id))}>
                                  <TrashIcon />
                                </IconBtn>
                              </div>
                            </div>
                          )}
                        </td>
                        {SS_SCORE_COLS.map((c) => (
                          <td key={c.value} className="border border-gray-300 bg-blue-50/40" />
                        ))}
                        <td className="border border-gray-300 bg-blue-50/40" />
                        <td className="border border-gray-300 bg-blue-50/40" />
                      </tr>
                    )
                  })}

                  {/* Add question inline row */}
                  {addingQIndId === ind.id && (
                    <tr key={`add-q-${ind.id}`} className="bg-yellow-50">
                      <td className="border border-gray-300 px-3 py-1.5" colSpan={SS_SCORE_COLS.length + 2}>
                        <div className="flex gap-2 items-center">
                          <textarea
                            autoFocus
                            value={newQText}
                            onChange={(e) => setNewQText(e.target.value)}
                            placeholder="Teks pertanyaan baru..."
                            className="border border-gray-300 rounded px-2 py-1 text-sm flex-1 resize-none"
                            rows={2}
                          />
                          <button onClick={() => { setAddingQIndId(null); setNewQText('') }} className="text-xs text-gray-500 px-1 hover:text-gray-700">Batal</button>
                          <button
                            disabled={!newQText.trim() || createQuestion.isPending}
                            onClick={() =>
                              createQuestion.mutate(
                                { indicatorId: ind.id, data: { question_text: newQText, order_no: questions.length + 1 } },
                                { onSuccess: () => { setAddingQIndId(null); setNewQText('') } }
                              )
                            }
                            className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            Tambah
                          </button>
                        </div>
                      </td>
                      <td className="border border-gray-300" />
                    </tr>
                  )}

                  {/* Summary rows */}
                  <tr key={`sub-${ind.id}`} className="bg-gray-50 text-center text-xs text-gray-500">
                    <td className="border border-gray-300 px-3 py-1 text-right font-medium">Subtotal</td>
                    {SS_SCORE_COLS.map((c) => <td key={c.value} className="border border-gray-300" />)}
                    <td className="border border-gray-300 text-left">
                      <button
                        onClick={() => { setAddingQIndId(ind.id); setNewQText('') }}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 px-1"
                      >
                        <PlusIcon /> Tambah pertanyaan
                      </button>
                    </td>
                    <td className="border border-gray-300" />
                  </tr>
                  <tr key={`rata-${ind.id}`} className="bg-[#BDD7EE]/40 font-semibold text-xs text-gray-800">
                    <td className="border border-gray-300 px-3 py-1.5 text-right" colSpan={2}>Skor Rata-rata</td>
                    <td className="border border-gray-300 text-center text-blue-700" colSpan={SS_SCORE_COLS.length}>—</td>
                    <td className="border border-gray-300" />
                  </tr>
                </>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MasterDataPage() {
  const [activeTab, setActiveTab] = useState<Tab>('akhlak')

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Master Data Penilaian</h1>
          <p className="text-sm text-gray-500 mt-1">
            Kelola indikator Soft Skill dan nilai AKHLAK yang digunakan dalam formulir penilaian
          </p>
        </div>
        <span className="text-xs font-semibold text-red-600 uppercase tracking-wide mt-1">
          Pribadi dan Rahasia
        </span>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {([
          { key: 'akhlak' as Tab, label: 'AKHLAK & Pengembangan Diri' },
          { key: 'ss' as Tab, label: 'Soft Skill' },
        ]).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'akhlak' && (
        <>
          <div className="mb-4 text-center text-xs text-gray-500 italic">
            Harap diisi dengan tanda (X) atau (V)
          </div>
          <AkhlakTab />
        </>
      )}

      {activeTab === 'ss' && <SSTab />}
    </div>
  )
}
